/**
 * EM Portal — Approved Assets page.
 * Approved KPMG publications from SME review and asset-manager uploads.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, TextField,
  InputAdornment, Grid,
} from '@mui/material';
import { CheckCircle, HourglassEmpty, Cancel, Search, Inventory } from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';

interface UploadedAsset {
  id: string;
  assessment_type: string;
  asset_class: string;
  filename: string;
  description: string | null;
  status: string;
  review_notes: string | null;
  uploaded_by: string | null;
  created_at: string | null;
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  pending: <HourglassEmpty fontSize="small" />,
  approved: <CheckCircle fontSize="small" />,
  rejected: <Cancel fontSize="small" />,
};

const STATUS_COLORS: Record<string, 'warning' | 'success' | 'error'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
};

const EM_COLOR = '#C6007E';

const UploadedAssets: React.FC = () => {
  const [assets, setAssets] = useState<UploadedAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchUploadedAssets = useCallback(() => {

    setLoading(true);
    setError(null);
    authFetch('/api/admin/portal/assets?status=approved')
      .then((r) => {
        if (!r.ok) return r.json().then(d => { throw new Error(d.error || `Server error ${r.status}`); });
        return r.json();
      })
      .then((data) => {
        if (data.success) setAssets(data.assets);
        else setError(data.error || 'Failed to load assets');
      })
      .catch((e) => setError(e.message || 'Failed to load uploaded assets'))
      .finally(() => { setLastRefreshed(new Date()); setLoading(false); });

  }, []);

  useEffect(() => { fetchUploadedAssets(); }, [fetchUploadedAssets]);

  const filtered = assets.filter((a) => {
    const term = searchTerm.toLowerCase();
    return (
      a.filename.toLowerCase().includes(term) ||
      a.assessment_type.toLowerCase().includes(term) ||
      a.asset_class.toLowerCase().includes(term) ||
      (a.description || '').toLowerCase().includes(term)
    );
  });

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Approved Assets
        </Typography>
        <RefreshButton onRefresh={fetchUploadedAssets} loading={loading} lastRefreshed={lastRefreshed} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View KPMG materials approved in Review SME Assets or published directly from Upload Assets.
        Client-visible materials are available as view-only in the client Asset Library.
      </Typography>

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Approved Assets', value: assets.length, color: EM_COLOR },
        ].map((card) => (
          <Grid item xs={6} sm={3} key={card.label}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: card.color }}>{card.value}</Typography>
              <Typography variant="body2" color="text.secondary">{card.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search by file name, type, or class..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><Search /></InputAdornment>
          ),
        }}
      />

      {filtered.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Inventory sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            {assets.length === 0 ? 'No assets have been approved yet.' : 'No assets match your search.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Assessment Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Asset Class</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Uploaded</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((asset) => (
                <TableRow key={asset.id} hover>
                  <TableCell>{asset.filename}</TableCell>
                  <TableCell>{asset.assessment_type}</TableCell>
                  <TableCell>
                    <Chip label={asset.asset_class} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={STATUS_ICONS[asset.status] as React.ReactElement}
                      label={asset.status}
                      size="small"
                      color={STATUS_COLORS[asset.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {asset.created_at ? new Date(asset.created_at).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {asset.description || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default UploadedAssets;
